import { CallToolResult } from '@modelcontextprotocol/sdk/types.js';
import { Ok } from 'ts-results-es';
import { z } from 'zod';

import {
  FeatureDisabledError,
  UnknownError,
  ViewNotAllowedError,
} from '../../../errors/mcpToolError.js';
import { useRestApi } from '../../../restApiInstance.js';
import { ProductVersion } from '../../../sdks/tableau/types/serverInfo.js';
import { WebMcpServer } from '../../../server.web.js';
import { resourceAccessChecker } from '../resourceAccessChecker.js';
import { WebTool } from '../tool.js';
import { getImageFormatForVersion } from './getImageFormatForVersion.js';
import {
  buildImageToolResult,
  ImageToolResult,
  imageToolResultToCallToolResult,
} from './imageToolResult.js';

const paramsSchema = {
  viewId: z.string(),
  width: z.number().gt(0).int().optional(),
  height: z.number().gt(0).int().optional(),
  format: z
    .enum(['PNG', 'SVG'])
    .optional()
    .describe(
      'The image format to return. Use "PNG" (default) when the image will be analyzed or interpreted. Use "SVG" when the image will be displayed to the user — SVG is scalable and produces smaller file sizes.',
    ),
  viewFilters: z
    .record(z.string())
    .optional()
    .describe('Optional map of view filter field names to values.'),
};

export const getGetViewImageTool = (
  server: WebMcpServer,
  tableauServerVersion: ProductVersion,
): WebTool<typeof paramsSchema> => {
  const getViewImageTool = new WebTool({
    server,
    name: 'get-view-image',
    description: [
      'Returns a static, non-interactive image of the specified view in a Tableau workbook.',
      'Use only when the user explicitly wants an image artifact — a screenshot, picture, thumbnail, PNG/PDF, or an image to embed in a document or export.',
      'For a bare "show me / open / explore this view" the user wants the interactive embed — use render-interactive-viz instead.',
      'Optional width and height in pixels control render size.',
      'Optional view field names and values can be provided to filter the view.',
      'For custom views, use the tool to get custom view image by custom view id instead.',
    ].join(' '),
    paramsSchema,
    annotations: {
      title: 'Get View Image',
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
    callback: async (
      { viewId, width, height, format, viewFilters },
      extra,
    ): Promise<CallToolResult> => {
      return await getViewImageTool.logAndExecute<ImageToolResult>({
        extra,
        args: { viewId, width, height, format, viewFilters },
        callback: async () => {
          const formatResult = getImageFormatForVersion(format, tableauServerVersion);
          if (formatResult.isErr()) {
            return formatResult;
          }

          const isViewAllowedResult = await resourceAccessChecker.isViewAllowed({
            viewId,
            extra,
          });

          if (!isViewAllowedResult.allowed) {
            return new ViewNotAllowedError(isViewAllowedResult.message).toErr();
          }

          const imageResult = await useRestApi({
            ...extra,
            jwtScopes: getViewImageTool.requiredApiScopes,
            callback: async (restApi) => {
              const result = await restApi.viewsMethods.queryViewImage({
                viewId,
                siteId: restApi.siteId,
                width,
                height,
                resolution: 'high',
                format: formatResult.value,
                viewFilters,
              });

              if (result.isErr()) {
                if (result.error.type === 'feature-disabled') {
                  return new FeatureDisabledError(
                    'The image format feature is disabled on this Tableau Server.',
                  ).toErr();
                }
                return new UnknownError(result.error.message, 400).toErr();
              }

              return new Ok(result.value);
            },
          });

          if (imageResult.isErr()) {
            return imageResult;
          }

          // Offload to S3 (returning a presigned URL) when configured, otherwise
          // carry the raw bytes for inline base64. Falls back to inline on any
          // S3 failure.
          return new Ok(
            await buildImageToolResult({
              imageData: imageResult.value,
              format,
              resourceId: viewId,
              config: extra.config,
              toolName: getViewImageTool.name,
              keyPrefixSegment: 'view-images/',
            }),
          );
        },
        constrainSuccessResult: (imageToolResult) => {
          return {
            type: 'success',
            result: imageToolResult,
          };
        },
        getSuccessResult: (imageToolResult) => imageToolResultToCallToolResult(imageToolResult),
      });
    },
  });

  return getViewImageTool;
};
